import { useState } from "react";
import type { AllowlistEntry } from "@iltocat/shared";
import { ApiError, api } from "../lib/api.js";
import { Notice } from "./Notice.js";
import { Panel } from "./Panel.js";

const day = (seconds: number) => new Date(seconds * 1000).toLocaleDateString();

/**
 * Who may create an account. Enough to invite people, which is all Stage 0
 * promises; the fuller console (storage, quotas) is Stage 5.
 */
export function AllowlistPanel({
  allowlist,
  onChange,
}: {
  allowlist: AllowlistEntry[];
  onChange: (next: AllowlistEntry[]) => void;
}) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(work: () => Promise<{ allowlist: AllowlistEntry[] }>) {
    setBusy(true);
    setError(null);
    try {
      onChange((await work()).allowlist);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  async function add(event: React.FormEvent) {
    event.preventDefault();
    await run(() => api.admin.addToAllowlist(email.trim(), note.trim()));
    setEmail("");
    setNote("");
  }

  return (
    <Panel title="Invite list" note="Only these addresses can create an account.">
      <form className="inline-add inline-add-wrap" onSubmit={add}>
        <input
          className="input"
          type="email"
          aria-label="Email to invite"
          placeholder="friend@example.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <input
          className="input"
          aria-label="Note (optional)"
          placeholder="Note, like “brother”"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <button className="button" type="submit" disabled={busy || email.trim().length === 0}>
          Invite
        </button>
      </form>
      {error && <Notice tone="error">{error}</Notice>}

      {allowlist.length === 0 ? (
        <p className="muted">Nobody is invited yet.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Address</th>
              <th>Note</th>
              <th>Added</th>
              <th>Signed in</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {allowlist.map((entry) => (
              <tr key={entry.email}>
                <td data-label="Address">
                  <strong>{entry.email}</strong>
                </td>
                <td data-label="Note" className="muted">
                  {entry.note}
                </td>
                <td data-label="Added" className="muted">
                  {day(entry.createdAt)}
                </td>
                <td data-label="Signed in">
                  <span className={entry.claimed ? "pill pill-ok" : "pill pill-warn"}>
                    {entry.claimed ? "Yes" : "Not yet"}
                  </span>
                </td>
                <td className="cell-action">
                  <button
                    className="button button-ghost button-small"
                    type="button"
                    disabled={busy}
                    onClick={() => void run(() => api.admin.removeFromAllowlist(entry.email))}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted small">
        Removing an address stops new sign-ups only. To lock out an existing account, disable it below.
      </p>
    </Panel>
  );
}
